/**
 * Linear-time percentile helpers on a bounded dataset (a few thousand items).
 * All helpers sort a copy so the input is untouched.
 */

function percentileFromSorted(sorted: number[], ratio: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(
    sorted.length - 1,
    Math.max(0, Math.floor(ratio * (sorted.length - 1))),
  );
  return sorted[idx] ?? 0;
}

/**
 * Compute multiple percentiles in a single sort pass.
 * Returns values in the same order as the `ratios` input.
 * Use this when you need p50 + p95 on the same array to avoid two sorts.
 */
export function percentiles(values: number[], ratios: number[]): number[] {
  if (values.length === 0) return ratios.map(() => 0);
  const sorted = [...values].sort((a, b) => a - b);
  return ratios.map((r) => percentileFromSorted(sorted, r));
}

export function p50(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return percentileFromSorted(sorted, 0.5);
}

export function p95(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return percentileFromSorted(sorted, 0.95);
}
